import { useCallback } from 'react';
import type { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  getInheritedSettings,
  getSourceAndLabel,
  updateChargeRateCents,
  updateProRateCents,
} from '../PositionRateSettingsForm.utils';

type UseMarginEffectProps = {
  source: string;
  inheritedSettings: any;
  watchedFields: {
    chargeRateCents: number;
    blendedRateCents: number;
    useBlendedRate: boolean;
    marginPercent: number | undefined;
    blendedRateMarkup: number | null | undefined;
    proRateCents: number;
    lockedRateType: 'lock_professional_rate' | 'lock_charge_rate';
  };
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  isW2Local: boolean;
  validateBaseMarginPercentW2?: (value: number) => void;
  validateMarginAgainstGuardrail?: (value: number) => void;
  blendedRateMarkup?: number;
};

export const useMarginEffect = ({
  source,
  inheritedSettings,
  watchedFields,
  setValue,
  watch,
  isW2Local,
  validateBaseMarginPercentW2,
  validateMarginAgainstGuardrail,
  blendedRateMarkup,
}: UseMarginEffectProps) => {
  const handleMarginChange = useCallback(
    (value: number) => {
      const isMarginValid = !isNaN(value) && value >= 0 && value < 1;
      if (!isMarginValid) {
        return;
      }

      const isLocationLevel = source.includes('location');

      let applicableRate =
        !isLocationLevel && watchedFields.useBlendedRate
          ? watchedFields.blendedRateCents
          : watchedFields.chargeRateCents;

      const rateTypeInheritedSettings = getInheritedSettings({ source, inheritedSettings, watch });

      if (!applicableRate) {
        applicableRate =
          !isLocationLevel && watchedFields.useBlendedRate
            ? rateTypeInheritedSettings.charge_rate_cents *
              (1 + (rateTypeInheritedSettings.blended_rates.blended_rate_markup || 0))
            : rateTypeInheritedSettings.charge_rate_cents;
      }

      if (watchedFields.lockedRateType === 'lock_professional_rate' && watchedFields.proRateCents) {
        updateChargeRateCents({
          setValue,
          watch,
          source,
          professionalRateCents: watchedFields.proRateCents,
          baseMarginPercent: value,
          inheritedSettings,
          blendedMarkup: watchedFields.blendedRateMarkup,
        });
      } else {
        updateProRateCents({
          setValue,
          watch,
          source,
          baseMarginPercent: value,
          rateCents: applicableRate,
          inheritedSettings,
        });
      }
    },
    [watchedFields, source, setValue, watch, inheritedSettings],
  );

  const handleLockTypeChange = useCallback(
    (lockType: 'lock_professional_rate' | 'lock_charge_rate') => {
      setValue(`${source}.lockedRateType`, lockType);

      const isMarginValid =
        !isNaN(watchedFields.marginPercent) &&
        watchedFields.marginPercent >= 0 &&
        watchedFields.marginPercent < 1;
      if (!isMarginValid) {
        return;
      }

      const isLocationLevel = source.includes('location');

      let applicableRate =
        !isLocationLevel && watchedFields.useBlendedRate
          ? watchedFields.blendedRateCents
          : watchedFields.chargeRateCents;
      const { field } = getSourceAndLabel(source, true);

      const rateTypeInheritedSettings = getInheritedSettings({ source, inheritedSettings, watch });
      const applicableMarginPercent =
        watchedFields.marginPercent || rateTypeInheritedSettings[field];

      if (!applicableRate) {
        applicableRate =
          !isLocationLevel && watchedFields.useBlendedRate
            ? rateTypeInheritedSettings.charge_rate_cents *
              (1 + (rateTypeInheritedSettings.blended_rates.blended_rate_markup || 0))
            : rateTypeInheritedSettings.charge_rate_cents;
      }

      if (lockType === 'lock_professional_rate' && watchedFields.proRateCents) {
        updateChargeRateCents({
          setValue,
          watch,
          source,
          professionalRateCents: watchedFields.proRateCents,
          baseMarginPercent: applicableMarginPercent,
          inheritedSettings,
          blendedMarkup: watchedFields.blendedRateMarkup,
        });
      } else if (lockType === 'lock_charge_rate') {
        updateProRateCents({
          setValue,
          watch,
          source,
          baseMarginPercent: applicableMarginPercent,
          rateCents: applicableRate,
          inheritedSettings,
        });
      }
    },
    [watchedFields, source, setValue, watch, inheritedSettings],
  );

  const handleRateStructureChange = useCallback(
    (useBlendedRate: boolean, level: string) => {
      const isW2 = source.includes('w2');

      if (isW2) {
        setValue(`${level}.w2.local.use_blended_rate`, useBlendedRate);
        setValue(`${level}.w2.travel.use_blended_rate`, useBlendedRate);
      }

      const isLocationLevel = source.includes('location');

      const applicableRate =
        !isLocationLevel && useBlendedRate
          ? watchedFields.blendedRateCents
          : watchedFields.chargeRateCents;

      updateProRateCents({
        setValue,
        watch,
        source,
        rateCents: applicableRate,
        inheritedSettings,
        baseMarginPercent: watchedFields.marginPercent,
      });
    },
    [source, setValue, watch, watchedFields, inheritedSettings],
  );

  const handleMarginChangeWithValidation = useCallback(
    (value: number) => {
      handleMarginChange(value);
      if (isW2Local && validateBaseMarginPercentW2) {
        validateBaseMarginPercentW2(value);
      }
      if (validateMarginAgainstGuardrail) {
        validateMarginAgainstGuardrail(value);
      }
    },
    [handleMarginChange, isW2Local, validateBaseMarginPercentW2, validateMarginAgainstGuardrail],
  );

  return {
    handleMarginChange: handleMarginChangeWithValidation,
    handleLockTypeChange,
    handleRateStructureChange,
  };
};
